import {
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

type Identifiable = { id: number };

type ActionColumn<T extends Identifiable> = {
  name: string;
  actions: (props: { row: T; index: number }) => ReactNode;
  width?: string;
};

function isActionColumn<T extends Identifiable>(
  col: unknown,
): col is ActionColumn<T> {
  return !!col && typeof col === 'object' && 'actions' in col;
}

type SimpleColumn<T extends Identifiable> = {
  name: string;
  isNumeric?: boolean;
  get: (row: T) => string;
  width?: string;
};

function isSimpleColumn<T extends Identifiable>(
  col: unknown,
): col is SimpleColumn<T> {
  return !!col && typeof col === 'object' && 'get' in col;
}

type RenderColumn<T extends Identifiable> = {
  name: string;
  isNumeric?: boolean;
  render: (props: { row: T; index: number }) => ReactNode;
  width?: string;
};

function isRenderColumn<T extends Identifiable>(
  col: unknown,
): col is RenderColumn<T> {
  return !!col && typeof col === 'object' && 'render' in col;
}

type Column<T extends Identifiable> =
  | ActionColumn<T>
  | SimpleColumn<T>
  | RenderColumn<T>;

export function DataTable<T extends Identifiable>(props: {
  data: T[];
  columns: Column<T>[];
  caption?: string;
}) {
  return (
    <TableContainer overflowY="auto" maxHeight="500px">
      <Table
        variant="simple"
        size="sm"
        __css={{ tableLayout: 'fixed', width: '100%' }}
      >
        {props.caption && <TableCaption>{props.caption}</TableCaption>}
        <Thead position="sticky" top={0} bg="gray.300" zIndex={100}>
          <Tr>
            {props.columns.map((col, index) => (
              <Th
                key={col.name}
                isNumeric={
                  isActionColumn(col)
                    ? true
                    : isSimpleColumn(col) || isRenderColumn(col)
                      ? col.isNumeric
                      : false
                }
                style={{
                  textAlign:
                    index === props.columns.length - 1 ? 'left' : 'inherit',
                  width: col.width,
                }}
              >
                {col.name}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {props.data.map((row, index) => (
            <Tr key={`row-${row.id}`}>
              {props.columns.map((col) => (
                <Td
                  key={`cell-${row.id}-${col.name}`}
                  isNumeric={
                    isSimpleColumn(col) || isRenderColumn(col)
                      ? col.isNumeric
                      : false
                  }
                  style={{
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: col.width,
                  }}
                >
                  {isActionColumn(col) && col.actions({ row, index })}
                  {isSimpleColumn(col) && col.get(row)}
                  {isRenderColumn(col) && col.render({ row, index })}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
